/**
 * AgentOsEshopLandingPage.jsx
 * ------------------------------------------------------------------
 * Funnel B — E-shop Owner landing page (cold traffic destination).
 *
 * Tone: ešte direct-to-consumer ako Funnel A. Hlavný hook: "Vyhoď
 * zamestnanca. Najmi si AI agenta." Pain-driven, ROI-driven,
 * lifestyle-aspirational obrázky v štýle Profitron Bot, ale CHARAKTEROVO
 * sa nesnažíme predávať tajné peniaze — predávame reálnu B2B substance.
 *
 * Lead capture: POST /api/agent-os/leads/eshop → enrolls into
 * `agent_os_eshop` drip sequence (4 emaily SK + CZ).
 */
import { useState, useMemo } from "react";
import axios from "axios";
import { API } from "../config";
import {
  ShoppingCart, ArrowRight, CheckCircle, AlertTriangle, Moon,
  Loader2, Sparkles, Lock, Zap, MessageCircle, Star, Mail, Globe2, Crown,
} from "lucide-react";
import {
  AgentOsExitPopup, LiveLeadCounter, CalendlySuccessEmbed, AgentOsPixelTracker,
} from "../components/agent-os/AgentOsShared";

const AGENT_OPTIONS = [
  { id: "helpdesk",      name: "Helpdesk Agent",      price: 99,  icon: "💬", saves: "1× zamestnanec (1 800 €)", demoHref: "/agent-os/demo/helpdesk" },
  { id: "review",        name: "Review Reply Agent",  price: 79,  icon: "⭐", saves: "30 min × 50 recenzií", demoHref: "/agent-os/demo/review-reply" },
  { id: "cart-recovery", name: "Cart Recovery Agent", price: 99,  icon: "🛒", saves: "12-18 % recovery rate", demoHref: "/agent-os/demo/cart-recovery" },
  { id: "product-desc",  name: "Product Desc Optimizer", price: 79,  icon: "✍️", saves: "SEO + AI Search traffic", demoHref: "/agent-os/demo/product-desc" },
  { id: "newsletter",    name: "Newsletter Agent",    price: 79,  icon: "📧", saves: "1× SMM (1 500 €)", demoHref: "/agent-os/demo/newsletter" },
  { id: "gdpr-watch",    name: "GDPR + A11y Watch",   price: 49,  icon: "🛡️", saves: "Denný compliance scan", demoHref: "/agent-os/demo/gdpr-watch" },
  { id: "social-media",  name: "Social Media Scheduler", price: 79, icon: "📱", saves: "1× SMM junior (1 000 €)", demoHref: "/agent-os/demo/social-media" },
  { id: "pricing-intel", name: "Pricing Intelligence", price: 149, icon: "📈", saves: "5-12 % margin lift", demoHref: "/agent-os/demo/pricing-intel" },
  { id: "customer-onboarding", name: "Customer Onboarding", price: 69, icon: "🎓", saves: "+25 % repeat purchase", demoHref: "/agent-os/demo/customer-onboarding" },
  { id: "review-moderator", name: "Review Moderator", price: 69, icon: "🚨", saves: "Detekuje fake recenzie", demoHref: "/agent-os/demo/review-moderator" },
  { id: "behavior-analytics", name: "Behavior Analytics", price: 129, icon: "📊", saves: "Týždenné AI návrhy opráv", demoHref: "/agent-os/demo/behavior-analytics" },
];

export default function AgentOsEshopLandingPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-emerald-500 selection:text-black" data-testid="agent-os-eshop-landing">
      <AgentOsPixelTracker event="ViewContent" />
      <AgentOsExitPopup variant="eshop" />
      <TopBar />
      <Hero />
      <PainSection />
      <BeforeAfterSection />
      <AgentPickerSection />
      <RoiCalculator />
      <TrustSection />
      <PricingSection />
      <FaqSection />
      <LeadFormSection />
      <Footer />
    </div>
  );
}

function TopBar() {
  return (
    <div className="border-b border-emerald-900/30 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="agent-os-eshop-logo">
          <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
            <ShoppingCart className="w-5 h-5 text-black" />
          </div>
          <span className="font-black tracking-tight">
            TopRankPulse <span className="text-emerald-400">· Agent OS</span>
          </span>
        </a>
        <a
          href="/agent-os/onboard"
          data-testid="agent-os-eshop-topbar-cta"
          className="px-4 py-2 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest"
        >
          Free trial 14 dní
        </a>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="agent-os-eshop-hero">
      <div className="absolute -top-32 -left-20 w-[500px] h-[500px] bg-emerald-500/10 blur-[120px] pointer-events-none" />
      <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[100px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24">
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-emerald-700/40 bg-emerald-900/10 mb-8" data-testid="agent-os-eshop-hero-badge">
          <Sparkles className="w-3 h-3 text-emerald-400" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-bold">
            14 dní zadarmo · bez karty · onboarding €0
          </span>
        </div>

        <div className="mb-6">
          <LiveLeadCounter variant="eshop" />
        </div>

        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] mb-6 max-w-4xl">
          Vyhoď zamestnanca.
          <br />
          Najmi si <span className="text-emerald-400">AI agenta.</span>
          <br />
          Za 1/10 ceny. 🤖
        </h1>

        <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mb-3 leading-relaxed">
          23:47. Tvoj support už dávno spí. V Heureke 3 nové 1★ recenzie. V košíku 18 produktov opustených.
        </p>
        <p className="text-lg sm:text-xl text-white max-w-2xl mb-10 leading-relaxed font-bold">
          AI agent počas tých 23:47 robí prácu. Po slovensky, nemecky, anglicky.
        </p>

        <div className="flex flex-wrap gap-4 mb-12">
          <a
            href="/agent-os/onboard"
            data-testid="agent-os-eshop-hero-primary-cta"
            className="inline-flex items-center gap-2 px-7 py-4 bg-emerald-400 hover:bg-emerald-300 text-black font-black uppercase tracking-wider text-sm transition-all shadow-[0_0_40px_rgba(52,211,153,0.3)]"
          >
            Skús zadarmo (14 dní) <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="#calculator"
            data-testid="agent-os-eshop-hero-secondary-cta"
            className="inline-flex items-center gap-2 px-7 py-4 border border-gray-700 hover:border-emerald-400 text-white font-bold uppercase tracking-wider text-sm transition-colors"
          >
            Spočítaj koľko ušetríš
          </a>
        </div>

        <div className="flex flex-wrap gap-8 text-xs text-gray-500 font-mono uppercase tracking-wider">
          <span><span className="text-emerald-400 font-black">24/7</span> nonstop</span>
          <span><span className="text-emerald-400 font-black">26</span> jazykov</span>
          <span><span className="text-emerald-400 font-black">€99</span> /mes per agent</span>
          <span><span className="text-emerald-400 font-black">0</span> sick days</span>
        </div>
      </div>
    </section>
  );
}

function PainSection() {
  const pains = [
    {
      icon: <Moon className="w-8 h-8 text-rose-400" />,
      title: "Polnoc, inbox plný",
      body: "Zákazník napísal o 23:14. Odpovieš mu ráno o 9:00. On si medzitým kúpil u konkurencie. Stratených €127.",
    },
    {
      icon: <Star className="w-8 h-8 text-rose-400" />,
      title: "1★ recenzia v Heureke",
      body: "Bez odpovede stojí 15 % konverzie produktu. Tvoj support tím to uvidí v pondelok. Cez víkend stratené ~€800.",
    },
    {
      icon: <ShoppingCart className="w-8 h-8 text-rose-400" />,
      title: "Košík abandoned",
      body: "Klaviyo recoveruje 5-8 %. AI Cart Recovery 12-18 %. Rozdiel: ~€2 000/mes pre priemerný e-shop.",
    },
  ];
  return (
    <section className="border-y border-gray-900 bg-black py-20 px-6" data-testid="agent-os-eshop-pain">
      <div className="max-w-6xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-rose-400 font-bold mb-3">Realita</div>
        <h2 className="text-3xl sm:text-4xl font-black mb-12 max-w-2xl">
          Toto sa stáva každú noc v tvojom e-shope. <span className="text-rose-400">Bez teba.</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {pains.map((p, i) => (
            <div key={i} data-testid={`agent-os-eshop-pain-${i}`} className="p-6 bg-gradient-to-br from-rose-950/20 to-black border border-rose-900/30 hover:border-rose-700/60 transition-colors">
              <div className="mb-4">{p.icon}</div>
              <h3 className="text-xl font-black mb-2">{p.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeforeAfterSection() {
  return (
    <section className="py-24 px-6 bg-gradient-to-b from-black to-[#0a0a0a]" data-testid="agent-os-eshop-shift">
      <div className="max-w-6xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">Posun</div>
        <h2 className="text-3xl sm:text-5xl font-black mb-12 max-w-3xl">
          1 zamestnanec vs <span className="text-emerald-400">1 AI agent</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-10">
          <div className="p-8 bg-black border border-gray-800">
            <div className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Zamestnanec</div>
            <div className="text-5xl font-black mb-1 text-gray-500 tabular-nums">€1 800</div>
            <div className="text-xs text-gray-600 mb-6">/mes (čistá ruka)</div>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex gap-3"><span className="text-rose-400">✗</span> Pracuje 8 hodín × 5 dní</li>
              <li className="flex gap-3"><span className="text-rose-400">✗</span> 1 jazyk</li>
              <li className="flex gap-3"><span className="text-rose-400">✗</span> Dovolenka 25 dní + 10 PN</li>
              <li className="flex gap-3"><span className="text-rose-400">✗</span> 3 mesiace onboarding</li>
              <li className="flex gap-3"><span className="text-rose-400">✗</span> 30 % šanca výpovede do 1 roka</li>
              <li className="flex gap-3"><span className="text-rose-400">✗</span> Plus poistky, daň, 13. plat</li>
            </ul>
          </div>
          <div className="p-8 bg-gradient-to-br from-emerald-950/30 to-black border-2 border-emerald-700/60 relative">
            <div className="absolute -top-3 -right-3 px-3 py-1 bg-emerald-400 text-black text-[10px] font-black uppercase tracking-widest">
              Agent OS
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-3">AI Agent</div>
            <div className="text-5xl font-black mb-1 text-emerald-400 tabular-nums">€99</div>
            <div className="text-xs text-gray-400 mb-6">/mes (vrátane všetkého)</div>
            <ul className="space-y-3 text-sm text-gray-300">
              <li className="flex gap-3"><span className="text-emerald-400">✓</span> Pracuje 24/7/365</li>
              <li className="flex gap-3"><span className="text-emerald-400">✓</span> 26 jazykov</li>
              <li className="flex gap-3"><span className="text-emerald-400">✓</span> 0 dovolenka, 0 PN</li>
              <li className="flex gap-3"><span className="text-emerald-400">✓</span> Onboarding 5 minút</li>
              <li className="flex gap-3"><span className="text-emerald-400">✓</span> 0 % šanca výpovede (ever)</li>
              <li className="flex gap-3"><span className="text-emerald-400">✓</span> All-inclusive</li>
            </ul>
            <div className="mt-6 pt-6 border-t border-emerald-900/50 flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-widest text-emerald-300">Ušetríš ročne</span>
              <span className="text-2xl font-black text-emerald-400 tabular-nums">€20 412</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AgentPickerSection() {
  return (
    <section className="py-24 px-6 bg-[#0a0a0a]" data-testid="agent-os-eshop-picker">
      <div className="max-w-6xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">
          Postav si svoj AI tím
        </div>
        <h2 className="text-3xl sm:text-5xl font-black mb-4">
          Vyber agentov. <span className="text-emerald-400">Aktivácia 5 minút.</span>
        </h2>
        <p className="text-lg text-gray-400 max-w-2xl mb-12">
          Začni s jedným, pridaj ďalších kedykoľvek. Free trial 14 dní.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AGENT_OPTIONS.map((a, i) => (
            <div
              key={a.id}
              data-testid={`agent-os-eshop-picker-${a.id}`}
              className="p-5 bg-black border border-gray-800 hover:border-emerald-700/60 transition-all hover:translate-y-[-2px] flex flex-col"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-3xl">{a.icon}</span>
                <span className="text-xs font-black text-emerald-400 tabular-nums">€{a.price}/mes</span>
              </div>
              <h3 className="text-base font-black mb-1">{a.name}</h3>
              <p className="text-sm text-gray-400 mb-3 flex-1">Nahradí: {a.saves}</p>
              {a.demoHref ? (
                <a
                  href={a.demoHref}
                  data-testid={`agent-os-eshop-picker-demo-${a.id}`}
                  className="inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-bold uppercase tracking-widest border border-emerald-700/40 text-emerald-300 hover:text-white hover:border-emerald-400 transition-colors"
                >
                  🎬 Vyskúšaj demo <ArrowRight className="w-3 h-3" />
                </a>
              ) : (
                <span className="inline-block px-3 py-2 text-xs uppercase tracking-widest text-gray-600 border border-gray-900 text-center">
                  Demo onedlho
                </span>
              )}
            </div>
          ))}
          <div className="p-5 bg-gradient-to-br from-emerald-950/30 to-black border border-emerald-700/40 flex flex-col justify-center items-center text-center">
            <Zap className="w-8 h-8 text-emerald-400 mb-3" />
            <p className="text-sm font-bold text-emerald-300">Bundle 3 za 2</p>
            <p className="text-xs text-gray-400 mt-1">Mix akýchkoľvek 3 agentov, tretí zdarma</p>
          </div>
        </div>

        <div className="text-center mt-10">
          <a
            href="/agent-os/demo/review-reply"
            data-testid="agent-os-eshop-demo-link"
            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-emerald-700/60 hover:border-emerald-400 bg-black/40 text-emerald-300 hover:text-white font-bold uppercase tracking-widest text-xs transition-colors"
          >
            🎬 Vyskúšaj Review Agenta naživo (bez registrácie) <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </section>
  );
}

function RoiCalculator() {
  const [orders, setOrders] = useState(200);
  const [teamCost, setTeamCost] = useState(3600);
  const [agents, setAgents] = useState(3);

  const { agentCost, monthlySave, yearlySave, ordersAutomated } = useMemo(() => {
    // Each order generates support touchpoints (returns, FAQ, review followup).
    // Conservative: 1 minute saved per order × €0.50/min (~ €30/h).
    const orderAutomationValue = orders * 0.5;
    const agentCost = agents * 99;
    const monthlySave = Math.max(0, teamCost + orderAutomationValue - agentCost);
    return {
      agentCost,
      monthlySave,
      yearlySave: monthlySave * 12,
      ordersAutomated: Math.round(orders * 0.7), // 70% touch-free rate
    };
  }, [agents, teamCost, orders]);

  return (
    <section id="calculator" className="py-24 px-6 bg-black border-y border-emerald-900/30" data-testid="agent-os-eshop-calculator">
      <div className="max-w-5xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">
          ROI Kalkulačka
        </div>
        <h2 className="text-3xl sm:text-5xl font-black mb-4">
          Koľko ti to <span className="text-emerald-400">ušetrí ročne?</span>
        </h2>
        <p className="text-lg text-gray-400 mb-12 max-w-2xl">
          Posuvníky podľa tvojho e-shopu. Výpočet real-time.
        </p>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-7">
            <SliderRow
              label="Objednávky / mes"
              value={orders}
              onChange={setOrders}
              min={10}
              max={2000}
              suffix="obj/mes"
              testid="agent-os-eshop-calc-orders"
              color="emerald"
            />
            <SliderRow
              label="Tvoj support team stojí (€/mes)"
              value={teamCost}
              onChange={setTeamCost}
              min={0}
              max={15000}
              suffix="€/mes"
              testid="agent-os-eshop-calc-team"
              color="emerald"
            />
            <SliderRow
              label="Koľko agentov chceš?"
              value={agents}
              onChange={setAgents}
              min={1}
              max={10}
              suffix="agentov"
              testid="agent-os-eshop-calc-agents"
              color="emerald"
            />
          </div>

          <div className="p-8 bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-700/60 space-y-5">
            <KpiRow label="AI agenti / mes" value={`€${agentCost}`} testid="agent-os-eshop-calc-agentcost" color="emerald" />
            <KpiRow label="Tvoj team / mes" value={`€${teamCost}`} testid="agent-os-eshop-calc-teamcost" color="emerald" />
            <KpiRow label="Auto-vyriešené objednávky" value={`${ordersAutomated.toLocaleString("sk-SK")}`} testid="agent-os-eshop-calc-automated" color="emerald" />
            <KpiRow label="Ušetríš mesačne" value={`€${monthlySave.toLocaleString("sk-SK")}`} accent big testid="agent-os-eshop-calc-monthly" color="emerald" />
            <hr className="border-emerald-900/40" />
            <KpiRow label="Ušetríš ročne" value={`€${yearlySave.toLocaleString("sk-SK")}`} accent big testid="agent-os-eshop-calc-yearly" color="emerald" />
            <a
              href="#trial"
              data-testid="agent-os-eshop-calc-cta"
              className="block w-full text-center px-6 py-3 bg-emerald-400 hover:bg-emerald-300 text-black font-black uppercase tracking-wider text-sm transition-colors"
            >
              Spusti free trial →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderRow({ label, value, onChange, min, max, suffix, testid, color = "emerald" }) {
  const accent = color === "emerald" ? "text-emerald-400 accent-emerald-400" : "text-yellow-400 accent-yellow-400";
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-sm mb-2">
        <span className="text-gray-400">{label}</span>
        <span className={`font-black tabular-nums ${accent.split(" ")[0]}`}>{value.toLocaleString("sk-SK")} {suffix}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`w-full ${accent.split(" ")[1]}`}
      />
    </div>
  );
}

function KpiRow({ label, value, accent, big, testid, color = "emerald" }) {
  const accentTxt = color === "emerald" ? "text-emerald-300" : "text-yellow-300";
  const accentVal = color === "emerald" ? "text-emerald-400" : "text-yellow-400";
  return (
    <div className="flex justify-between items-baseline" data-testid={testid}>
      <span className={`text-xs uppercase tracking-widest ${accent ? accentTxt : "text-gray-500"}`}>{label}</span>
      <span className={`tabular-nums font-black ${big ? "text-4xl" : "text-xl"} ${accent ? accentVal : "text-white"}`}>{value}</span>
    </div>
  );
}

function TrustSection() {
  const logos = ["Heureka", "Packeta", "Pohoda", "GLS", "WooCommerce", "Shopify"];
  return (
    <section className="py-16 px-6 bg-[#0a0a0a] border-y border-gray-900" data-testid="agent-os-eshop-trust">
      <div className="max-w-6xl mx-auto">
        <p className="text-center text-xs uppercase tracking-[0.3em] text-gray-500 font-bold mb-8">
          Native integrácie · SK/CZ moat
        </p>
        <div className="flex flex-wrap justify-center items-center gap-6 lg:gap-12">
          {logos.map((l) => (
            <div key={l} className="px-4 py-2 border border-gray-800 text-gray-400 font-bold tracking-wider uppercase text-xs hover:border-emerald-700/40 hover:text-emerald-300 transition-colors" data-testid={`agent-os-eshop-trust-${l.toLowerCase()}`}>
              {l}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const tiers = [
    { name: "1 Agent",  price: 99,  desc: "Začni s jedným",   features: ["1 AI agent", "Free trial 14 dní", "Email support"], testid: "tier-1" },
    { name: "Bundle 3", price: 198, originalPrice: 297, desc: "3 agenti za cenu 2", features: ["3 AI agenti", "Onboarding zdarma", "Priority support"], highlight: true, testid: "tier-3" },
    { name: "All-Star", price: 499, desc: "Všetkých 5+", features: ["Všetkých 20 agentov", "Dedicated success", "Custom integrácie"], testid: "tier-all" },
  ];
  return (
    <section id="trial" className="py-24 px-6 bg-black border-t border-emerald-900/30" data-testid="agent-os-eshop-pricing">
      <div className="max-w-6xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">Ceny</div>
        <h2 className="text-3xl sm:text-5xl font-black mb-4">
          Bez karty. Bez záväzku. <span className="text-emerald-400">14 dní zdarma.</span>
        </h2>
        <p className="text-lg text-gray-400 max-w-2xl mb-12">
          Ak ti to do 14 dní nesedí, prestaneš platiť. Žiadne otázky.
        </p>
        <div className="grid lg:grid-cols-3 gap-6">
          {tiers.map((t) => (
            <div
              key={t.name}
              data-testid={`agent-os-eshop-${t.testid}`}
              className={`relative p-8 border-2 transition-all ${
                t.highlight
                  ? "bg-gradient-to-br from-emerald-950/30 to-black border-emerald-500 shadow-[0_0_60px_rgba(52,211,153,0.15)]"
                  : "bg-black border-gray-800 hover:border-emerald-700/40"
              }`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-400 text-black text-[10px] font-black uppercase tracking-widest">
                  Najobľúbenejší
                </div>
              )}
              <h3 className="text-xl font-black mb-1">{t.name}</h3>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-5xl font-black tabular-nums">€{t.price}</span>
                <span className="text-gray-500 text-sm">/mes</span>
                {t.originalPrice && (
                  <span className="text-gray-600 line-through text-sm">€{t.originalPrice}</span>
                )}
              </div>
              <p className="text-sm text-gray-400 mb-6">{t.desc}</p>
              <ul className="space-y-2 mb-8">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-gray-300">
                    <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="#lead-form"
                data-testid={`agent-os-eshop-${t.testid}-cta`}
                className={`block w-full text-center px-6 py-3 font-black uppercase tracking-wider text-sm transition-colors ${
                  t.highlight ? "bg-emerald-400 hover:bg-emerald-300 text-black" : "border border-gray-700 hover:border-emerald-400 text-white"
                }`}
              >
                Skús zadarmo →
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const faqs = [
    { q: "Naozaj 14 dní zadarmo bez karty?", a: "Áno. Vyplníš email, aktivuješ agenta, pracuje 14 dní bez fakturácie. Po 14 dňoch sa pýtame, či chceš pokračovať. Ak nie, koniec — bez záväzku." },
    { q: "Hovorí AI Helpdesk Agent po slovensky?", a: "Áno, 26 jazykov vrátane SK/CZ/PL/DE/HU. AI rozpozná jazyk zákazníka a odpovie v ňom. Tón a štýl si nastavíš sám." },
    { q: "Bude zákazník vedieť, že hovorí s AI?", a: "Default-on hlavička v emaili ti dovolí povedať „odpovedá ti AI asistent“ alebo skryť to. Záleží na tebe." },
    { q: "Aké platby a integrácie podporuje?", a: "Stripe, GoPay, Apple Pay, Google Pay. Integrácie: WooCommerce, Shopify, Prestashop, Magento, vlastné API. Plus SK/CZ-native: Pohoda, MoneyS3, Heureka, Packeta, GLS." },
    { q: "Čo ak AI urobí chybu?", a: "Každý agent má human-in-the-loop volič — pred odoslaním si chceš schválovať odpovede? Áno/nie. Aj pri full-auto móde môžeš kedykoľvek pretočiť späť." },
  ];
  return (
    <section className="py-24 px-6 bg-[#0a0a0a]" data-testid="agent-os-eshop-faq">
      <div className="max-w-3xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">FAQ</div>
        <h2 className="text-3xl sm:text-5xl font-black mb-12">Najčastejšie otázky</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <details key={i} data-testid={`agent-os-eshop-faq-${i}`} className="group p-6 bg-black border border-gray-800 hover:border-emerald-700/40 transition-colors">
              <summary className="cursor-pointer flex justify-between items-center font-bold text-base">
                {f.q}
                <span className="text-emerald-400 group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function LeadFormSection() {
  const [form, setForm] = useState({
    email: "",
    shop_url: "",
    monthly_orders: "",
    biggest_pain_agents: [],
    team_cost_eur: 0,
  });
  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  const toggleAgent = (id) => {
    setForm((f) => {
      const cur = new Set(f.biggest_pain_agents);
      cur.has(id) ? cur.delete(id) : cur.add(id);
      return { ...f, biggest_pain_agents: Array.from(cur) };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setStatus({ loading: true, success: false, error: null });
    try {
      const params = new URLSearchParams(window.location.search);
      const source = params.get("utm_source") || params.get("source") || "organic";
      await axios.post(`${API}/agent-os/leads/eshop`, { ...form, source });
      setStatus({ loading: false, success: true, error: null });
    } catch (err) {
      setStatus({ loading: false, success: false, error: err?.response?.data?.detail || "Niečo zlyhalo." });
    }
  };

  if (status.success) {
    return (
      <section id="lead-form" className="py-24 px-6 bg-gradient-to-b from-black to-emerald-950/20" data-testid="agent-os-eshop-lead-success">
        <AgentOsPixelTracker event="Lead" value={99} />
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
          <h2 className="text-4xl font-black mb-4">Hotovo! 🚀</h2>
          <p className="text-lg text-gray-400 mb-4">
            Práve ti posielame personalizovaný audit + onboarding email.
            Skontroluj <strong className="text-white">spam priečinok</strong>.
          </p>
          <CalendlySuccessEmbed accent="emerald" />
          <a
            href="/agent-os/demo/review-reply"
            data-testid="agent-os-eshop-lead-success-demo-cta"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 border border-gray-700 hover:border-emerald-400 text-white font-bold uppercase tracking-wider text-sm"
          >
            Vyskúšaj demo agenta naživo <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="lead-form" className="py-24 px-6 bg-gradient-to-b from-black to-emerald-950/10" data-testid="agent-os-eshop-lead-form">
      <div className="max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">Spusti zadarmo</div>
        <h2 className="text-3xl sm:text-5xl font-black mb-4">
          Free trial za <span className="text-emerald-400">60 sekúnd.</span>
        </h2>
        <p className="text-lg text-gray-400 mb-10">
          Vyplň → personalizovaný audit do 5 minút → 14 dní zdarma. Bez karty.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <Input testid="lead-eshop-email" type="email" required placeholder="Tvoj email *" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Input testid="lead-eshop-url" type="text" placeholder="URL e-shopu (https://...)" value={form.shop_url} onChange={(v) => setForm({ ...form, shop_url: v })} />
          <Select
            testid="lead-eshop-orders"
            placeholder="Objednávky / mes"
            value={form.monthly_orders}
            onChange={(v) => setForm({ ...form, monthly_orders: v })}
            options={["<50", "50-200", "200-500", "500+"]}
          />
          <Input
            testid="lead-eshop-teamcost"
            type="number"
            placeholder="Tvoj support team stojí €/mes (voliteľné)"
            value={form.team_cost_eur || ""}
            onChange={(v) => setForm({ ...form, team_cost_eur: Number(v) || 0 })}
          />

          <div>
            <p className="text-sm font-bold text-gray-300 mb-3">Ktorých agentov chceš najprv?</p>
            <div className="grid grid-cols-2 gap-2">
              {AGENT_OPTIONS.map((a) => {
                const checked = form.biggest_pain_agents.includes(a.id);
                return (
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => toggleAgent(a.id)}
                    data-testid={`agent-os-eshop-lead-agent-${a.id}`}
                    className={`p-3 text-left border transition-all ${
                      checked
                        ? "bg-emerald-950/40 border-emerald-500 text-white"
                        : "bg-black border-gray-800 text-gray-400 hover:border-emerald-700/40"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold">{a.icon} {a.name}</span>
                      {checked && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {status.error && (
            <div className="p-3 bg-rose-950/30 border border-rose-700/40 text-rose-200 text-sm" data-testid="agent-os-eshop-lead-error">
              {status.error}
            </div>
          )}

          <button
            type="submit"
            disabled={status.loading}
            data-testid="agent-os-eshop-lead-submit"
            className="w-full px-6 py-4 bg-emerald-400 hover:bg-emerald-300 disabled:opacity-50 text-black font-black uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
          >
            {status.loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Posielam…</>
              : <>Spustiť free trial <ArrowRight className="w-4 h-4" /></>}
          </button>

          <div className="flex items-center gap-2 text-xs text-gray-500 justify-center pt-3">
            <Lock className="w-3 h-3" /> Tvoje dáta sú v bezpečí · GDPR · Bez karty · 0 spam
          </div>
        </form>
      </div>
    </section>
  );
}

function Input({ value, onChange, placeholder, type = "text", required = false, testid }) {
  return (
    <input
      data-testid={testid}
      type={type}
      required={required}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 bg-black border border-gray-800 focus:border-emerald-400 outline-none text-white placeholder-gray-600 transition-colors"
    />
  );
}

function Select({ value, onChange, placeholder, options, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 bg-black border border-gray-800 focus:border-emerald-400 outline-none text-white"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-900 py-10 px-6 text-center text-xs text-gray-600">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Crown className="w-4 h-4 text-emerald-400" />
        <span className="font-black text-white">TopRankPulse · Agent OS</span>
      </div>
      <p>© 2026 TopRankPulse. Najmi si AI, nie ľudí.</p>
      <div className="mt-3 flex justify-center gap-4 text-gray-700">
        <a href="/privacy" className="hover:text-emerald-400">Privacy</a>
        <a href="/terms"   className="hover:text-emerald-400">Terms</a>
        <a href="/gdpr"    className="hover:text-emerald-400">GDPR</a>
      </div>
    </footer>
  );
}
